import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, saveToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AriaOrb } from "../components/AriaOrb";
import { Shield, ChevronRight } from "lucide-react";
import { toast } from "sonner";

/**
 * Simulated IdP login page. Looks like an enterprise SSO sign-in screen.
 * In a real OKTA/SAML flow this would be hosted by the IdP and assert SAML
 * back to /sso/callback. Here we mimic the UX end-to-end for demos.
 */
export default function SsoIdp() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const orgId = params.get("org");
  const [orgName, setOrgName] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("creds"); // creds | mfa | bridging

  useEffect(() => {
    (async () => {
      try {
        // We don't have a public org lookup; just decorate.
        setOrgName("Your Organization");
      } catch (err) { console.error("[ARIA]", err); }
    })();
  }, [orgId]);

  const signIn = async (e) => {
    e?.preventDefault();
    if (!email || !email.includes("@")) { toast.error("Enter your work email"); return; }
    setBusy(true);
    setStage("mfa");
    // simulate MFA push
    await new Promise(r => setTimeout(r, 1100));
    setStage("bridging");
    await new Promise(r => setTimeout(r, 700));
    try {
      const { data } = await api.post("/sso/callback", {
        org_id: orgId,
        email,
        name: name || email.split("@")[0],
        sso_token: "simulated_assertion_" + Math.random().toString(36).slice(2),
      });
      saveToken(data.token);
      await checkAuth();
      navigate("/app/chat");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "SSO sign-in failed");
      setBusy(false); setStage("creds");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#18181B] text-white p-6">
      <div className="absolute top-6 left-6 text-xs text-slate-400 flex items-center gap-2">
        <Shield size={14} /> SECURE SSO · TLS 1.3
      </div>
      <div className="absolute top-6 right-6 text-xs text-slate-400 font-mono">id.{orgName.toLowerCase().replace(/\s+/g,"-") || "org"}.okta.com</div>

      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center mb-4">
            <AriaOrb size={32} />
          </div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-400 mb-2">Identity Provider</div>
          <h1 className="font-serif text-3xl">{orgName}</h1>
          <div className="text-sm text-slate-400 mt-1">Sign in to continue to ARIA</div>
        </div>

        <div className="bg-white/[0.04] backdrop-blur-sm border border-white/10 rounded-lg p-7">
          {stage === "creds" && (
            <form onSubmit={signIn} className="space-y-4" data-testid="idp-form">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400">Work email</label>
                <input data-testid="idp-email" value={email} onChange={e=>setEmail(e.target.value)} type="email" required
                  placeholder="you@example.com"
                  className="w-full mt-1 px-3 py-2.5 rounded-md bg-white/[0.06] border border-white/10 focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/30 outline-none text-white placeholder:text-slate-500" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-400">Full name (optional)</label>
                <input value={name} onChange={e=>setName(e.target.value)} type="text"
                  placeholder="Jane Scientist"
                  className="w-full mt-1 px-3 py-2.5 rounded-md bg-white/[0.06] border border-white/10 focus:border-[#2563FF] focus:ring-2 focus:ring-[#2563FF]/30 outline-none text-white placeholder:text-slate-500" />
              </div>
              <button data-testid="idp-submit" type="submit" disabled={busy}
                className="w-full py-2.5 rounded-md bg-[#2563FF] text-white hover:bg-[#0B3BCE] font-medium flex items-center justify-center gap-2 disabled:opacity-50">
                Continue <ChevronRight size={16} />
              </button>
              <div className="text-[11px] text-slate-500 text-center pt-2">
                Single sign-on routed through your organization's identity provider.
              </div>
            </form>
          )}
          {stage === "mfa" && (
            <div className="text-center py-6">
              <AriaOrb state="listening" size={56} className="mx-auto mb-4" />
              <div className="font-serif text-xl mb-1">Waiting for MFA</div>
              <div className="text-sm text-slate-400">Open your authenticator app and tap <strong className="text-white">Approve</strong>…</div>
            </div>
          )}
          {stage === "bridging" && (
            <div className="text-center py-6">
              <AriaOrb state="thinking" size={56} className="mx-auto mb-4" />
              <div className="font-serif text-xl mb-1">Bridging to ARIA</div>
              <div className="text-sm text-slate-400">Issuing your token…</div>
            </div>
          )}
        </div>

        <div className="text-center text-[11px] text-slate-500 mt-8">
          Protected by your IdP. <a href="/login" className="text-[#2563FF] hover:underline">Use password instead</a>
        </div>
      </div>
    </div>
  );
}
